import { ChangeDetectorRef, Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { interval, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { BedrockApiService } from '../../services/BedrockApiService';
import { TestPromptResponse } from '../../responses/testPromptResponse';
import { FileItem } from '../../models/fileItem';
import { base64ToBlob } from '../../helpers/blob.utils';
import { FileDefinitionService } from '../../services/FileDefinitionService';
import { FileDefinition } from '../../models/fileDefinition';

//Tests the prompt of an AI moov step with a test file (bedrockapi testPrompt, processed by the AI agent worker).
//The test can take several minutes when no AI worker is running yet, so: start the test, then poll until it is done.
//Result: output file (csv shown like show-file, zip as download only) and log file, each in its own tab.
@Component({ standalone:false,
  selector: 'test-ai-prompt-dialog',
  templateUrl: 'test-ai-prompt-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['test-ai-prompt-dialog.component.scss']
})
export class TestAIPromptDialogComponent implements OnDestroy {
  public title: string = "Test AI Prompt";
  public prompt: string = "";
  public runButtonText = "Run Test";
  public runAgainButtonText = "Run Again";
  public cancelButtonText = "Close";
  public errormessage: string = "";

  public selectedFile?: File;
  private moovDefinitionId?: number; //for billing; undefined for a new, not yet saved definition
  //output layout for the test: preselected with the file definition of the apply file definition step after the AI step, if any
  public outputFileDefinitionId?: number;
  public fileDefinitions: FileDefinition[] = [];
  public running: boolean = false;
  public elapsedSeconds: number = 0;
  public result?: TestPromptResponse;
  public outputFileItem?: FileItem; //csv output, shown with app-show-file
  public outputZipBlob?: Blob;      //zip output, download only
  public logFileItem?: FileItem;

  private readonly maxFileBytes = 5 * 1024 * 1024; //same limit as bedrockapi
  private readonly pollIntervalMs = 3000;
  private readonly maxWaitSeconds = 600;
  private polling: boolean = false;
  private stop$ = new Subject<void>(); //stops elapsed timer and polling

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private bedrockService: BedrockApiService, private fdService: FileDefinitionService, private dialogRef: MatDialogRef<TestAIPromptDialogComponent>, private cd: ChangeDetectorRef) {
    if(data){
      this.title = data.title || this.title;
      this.prompt = data.prompt ?? "";
      this.selectedFile = data.file; //test file of the previous test, if any
      this.moovDefinitionId = data.moovDefinitionId;
      this.outputFileDefinitionId = data.outputFileDefinitionId;
    }
  }

  ngOnInit(){
    //start an AI worker in the background while the user picks the test file
    this.bedrockService.testPromptWarmup().pipe(take(1)).subscribe();
    //same choice as for an apply file definition step; the preselected one is always offered
    this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
      this.fileDefinitions = (fdArray ?? [])
        .filter(fd=>(!fd.deleted && fd.published) || fd.id==this.outputFileDefinitionId)
        .sort((a,b)=>(a.name ?? '').localeCompare(b.name ?? ''));
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(){
    this.stop$.next();
    this.stop$.complete();
  }

  get canRun(){
    return !!this.selectedFile && !!this.prompt?.trim() && !this.running;
  }

  get elapsedText(){
    const minutes = Math.floor(this.elapsedSeconds / 60);
    const seconds = this.elapsedSeconds % 60;
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  get statusLabel(){
    switch(this.result?.status){
      case 'success': return 'Success';
      case 'partial': return 'Partially processed';
      default: return 'Failed';
    }
  }

  get statusClass(){
    return this.result?.status == 'success' ? 'primary-color' : 'warn-color';
  }

  get outputZipSizeText(){
    return this.outputZipBlob ? Math.ceil(this.outputZipBlob.size / 1024) + ' KB' : '';
  }

  fileInputChange(fileInputEvent: any) {
    this.errormessage = "";
    const file: File = fileInputEvent.target.files[0];
    if(!file) return;
    //a zip may only contain csv files; its content is checked by the server
    const fileName = file.name.toLowerCase();
    if(!fileName.endsWith('.csv') && !fileName.endsWith('.zip')){
      this.errormessage = "Only csv files or zip files of csv files can be tested";
      return;
    }
    if(file.size > this.maxFileBytes){
      this.errormessage = "The test file must not be larger than 5 MB";
      return;
    }
    this.selectedFile = file;
  }

  onRunClick(){
    if(!this.canRun) return;
    this.resetResult();
    this.running = true;
    this.elapsedSeconds = 0;
    interval(1000).pipe(takeUntil(this.stop$)).subscribe(() => {
      this.elapsedSeconds++;
      if(this.elapsedSeconds > this.maxWaitSeconds){
        this.finish("The test is taking unusually long. Please try again later.");
      }
      this.cd.detectChanges();
    });
    this.bedrockService.testPrompt(this.selectedFile!, this.prompt, this.moovDefinitionId, this.outputFileDefinitionId).pipe(take(1)).subscribe(r => this.processResponse(r as TestPromptResponse));
  }

  onCancelClick(){
    this.stop$.next(); //the test may keep running on the server, its result is just not shown
    //return the test file so the next test does not need to select it again
    this.dialogRef.close(this.selectedFile);
  }

  onDownloadZipClick(): void {
    const downloadAncher = document.createElement("a");
    downloadAncher.style.display = "none";
    downloadAncher.href = URL.createObjectURL(this.outputZipBlob!);
    downloadAncher.download = this.result!.outputFile!.fileName;
    downloadAncher.click();
  }

  private processResponse(r: TestPromptResponse){
    if(!this.running) return; //cancelled or timed out meanwhile
    if(!r || !r.success || r.state == 'failed'){
      this.finish(r?.message || "The test failed. Please try again later.");
    }
    else if(r.state == 'done'){
      this.finish("");
      this.showResult(r);
    }
    else if(!this.polling){
      //pending: ask again every few seconds until the result is there
      this.polling = true;
      timer(this.pollIntervalMs, this.pollIntervalMs).pipe(
        switchMap(() => this.bedrockService.getTestPrompt(r.testId!)),
        takeUntil(this.stop$)
      ).subscribe(pr => this.processResponse(pr as TestPromptResponse));
    }
  }

  private finish(errormessage: string){
    this.stop$.next();
    this.running = false;
    this.polling = false;
    this.errormessage = errormessage;
    this.cd.detectChanges();
  }

  private async showResult(r: TestPromptResponse){
    if(r.outputFile){
      if(r.outputFile.fileName.toLowerCase().endsWith('.zip')){
        this.outputZipBlob = await base64ToBlob(r.outputFile.base64File, 'application/zip');
      }
      else{
        const blob = await base64ToBlob(r.outputFile.base64File, 'text/plain');
        this.outputFileItem = new FileItem(blob, r.outputFile.fileName, undefined, undefined, false, undefined, undefined, undefined, undefined, undefined);
      }
    }
    if(r.logFile){
      const blob = await base64ToBlob(r.logFile.base64File, 'text/plain');
      this.logFileItem = new FileItem(blob, r.logFile.fileName, undefined, undefined, false, undefined, undefined, undefined, undefined, undefined);
    }
    this.result = r;
    this.cd.detectChanges();
  }

  private resetResult(){
    this.errormessage = "";
    this.result = undefined;
    this.outputFileItem = undefined;
    this.outputZipBlob = undefined;
    this.logFileItem = undefined;
  }
}
